import React from 'react';
import { withTranslation, WithTranslation } from '@wix/wix-i18n-config';
import s from './PackagePrice.scss';
import Price from './Price';
import FeatureList from '../FeatureList';
import {
  TaxTypes,
  TypeCycle,
  TypeSelectedOffer,
} from '../../../../types/general-types';
import PackageCycleModal from '../../../PackageCycleModal/PackageCycleModal';
import { biLogger } from '../../../../globals';
import {
  getQueryParam,
  removeDrilldownParams,
} from '../../../../services/window-utils';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { Tooltip } from 'wix-style-react';
import classnames from 'classnames';

export interface PackagePriceProps
  extends WithTranslation,
    InjectedExperimentsProps {
  amount: number;
  currency: string;
  cycles: TypeCycle[];
  productId: string;
  packageName: string;
  selectedOffer: TypeSelectedOffer;
  isEnabled: boolean;
  productIndex: number;
  currentPlan: boolean;
  discountAmount?: number;
  taxType?: TaxTypes;
  isCtaPrimary?: boolean;
  isSaleOn: boolean;
}

interface PackagePriceState {
  productCycleModalIsOpen: boolean;
}

class PackagePrice extends React.Component<
  PackagePriceProps,
  PackagePriceState
> {
  constructor(props: PackagePriceProps) {
    super(props);
    this.state = {
      productCycleModalIsOpen: false,
    };
  }

  componentDidMount() {
    const param = getQueryParam().get('productId');
    if (param) {
      this.setState({
        productCycleModalIsOpen:
          parseInt(param, 10) === this.props.productIndex,
      });
    }
  }

  onClickSelectProduct = () => {
    const isChecked =
      this.props.productId === 'bcbc441f-47da-4735-b58f-e5386647bf5f';
    this.setState({ productCycleModalIsOpen: true });
    biLogger.ppSelected({
      buttonType: 'regular',
      package_name: this.props.packageName,
      packageId: this.props.productId,
      isChecked,
    });
  };

  closeModalHandler = () => {
    removeDrilldownParams();
    this.setState({ productCycleModalIsOpen: false });
  };

  TaxMessage = () =>
    this.props.taxType === TaxTypes.VAT ? (
      <div className={s.tax} data-hook="packagePrice-tax">
        {this.props.t('package.taxVAT')}
      </div>
    ) : this.props.taxType === TaxTypes.GST ? (
      <div className={s.tax} data-hook="packagePrice-tax">
        {this.props.t('package.taxGST')}
      </div>
    ) : null;

  render() {
    const {
      t,
      amount,
      currency,
      cycles,
      productId,
      packageName,
      isEnabled,
      productIndex,
      discountAmount,
      taxType,
      currentPlan,
      isSaleOn,
    } = this.props;

    const selectedOffer = {
      ...this.props.selectedOffer,
      productId,
      productName: packageName,
    };

    const ctaTooltipContent = () => {
      return `${
        currentPlan ? t('compare.header.ctaTooltip.currentUpgrade') : ''
      }${t('compare.header.ctaTooltip.upgrade')}`;
    };

    return (
      <div className={s.root} data-hook="packagePrice">
        <div
          className={classnames(s.price, {
            [s.saleOn]: isSaleOn,
          })}
          data-hook="packagePrice-price"
        >
          <Price
            amount={amount}
            discountAmount={discountAmount}
            currency={currency}
          />
        </div>
        <div className={s.bottom}>
          <this.TaxMessage />
          <Tooltip
            content={ctaTooltipContent()}
            disabled={isEnabled || false}
            placement="bottom"
            maxWidth={244}
            dataHook="compare-header-tooltip"
            appendTo="window"
          >
            <div className={s.selectPrimary}>
              <button
                onClick={this.onClickSelectProduct}
                disabled={!isEnabled}
                data-hook="packagePrice-select"
              >
                <div
                  tabIndex={0}
                  role="button"
                  aria-pressed="false"
                  aria-label={`${t('package.select')} package ${packageName}`}
                  style={{ outline: 'none' }}
                >
                  {t('package.select')}
                </div>
              </button>
            </div>
          </Tooltip>
          <div
            className={s.highlightTitle}
            data-hook="packagePrice-highlightTitle"
          >
            {t(`highlights.featuresTitle.${productIndex}`)}
          </div>
          <div className={s.featureList} data-hook="packagePrice-featureList">
            <FeatureList productIndex={productIndex} />
          </div>
        </div>

        <PackageCycleModal
          isOpen={this.state.productCycleModalIsOpen}
          onClose={this.closeModalHandler}
          cycles={cycles}
          currency={currency}
          selectedOffer={selectedOffer}
          productIndex={productIndex}
          taxType={taxType}
        />
      </div>
    );
  }
}

export default withTranslation()(withExperiments(PackagePrice));
